import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { Card, CardHeader, CardBody, Col } from 'reactstrap';
import firebase from '@/config/firebase.config';
import AppLogo from '@/assets/img/brand/logo.jpeg';
import { Input } from '@/components/Input';
import { Button, buttonVariants } from '@/components/Button';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { authActionTypes } from '@store/Auth/Auth.actionTypes';
import styles from './ClientInvitation.module.scss';

// Matches the Signup form's password policy (src/views/Auth/signup/form.js).
const PASSWORD_POLICY_REGEX =
  /^(?=.*[!@#$&*])(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z]).{6,}$/;
const PASSWORD_POLICY_MESSAGE =
  'At least 6 characters, with one uppercase letter, one number and one special character';

const InviteCardShell = ({ children }) => (
  <Col lg="5" md="7">
    <Card className="bg-white shadow border-0">
      <CardHeader className="bg-transparent border-0">
        <div className="text-center">
          <img className="auth-app-logo" src={AppLogo} alt="Prodaff" />
        </div>
        <div className={styles.inviteHeader}>
          <Text
            variant={textVariantsEnum.Text_25}
            fontWeight={fontWeightEnum.Semibold}
            style={{ color: '#000' }}
          >
            Client Invitation
          </Text>
          <Text
            variant={textVariantsEnum.Display_xs}
            style={{ color: '#343942' }}
          >
            View a team&apos;s dashboard on Prodaff
          </Text>
        </div>
      </CardHeader>
      <CardBody className="px-lg-5 pb-lg-5 pt-lg-1">{children}</CardBody>
    </Card>
  </Col>
);

export const ClientInvitationComponent = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    i: inviteId,
    t: inviteToken,
    e: email,
  } = queryString.parse(location.search);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState({ password: '', confirmPassword: '' });

  const signUpAuth = useSelector((state) => ({ ...state.auth.signUp }));

  const currentUser = firebase.auth().currentUser;
  const signedInAsDifferentAccount =
    currentUser && email && currentUser.email?.toLowerCase() !== email;

  if (!inviteId || !inviteToken || !email) {
    return (
      <InviteCardShell>
        <Text
          variant={textVariantsEnum.Text_15}
          fontWeight={fontWeightEnum.Regular}
        >
          This invitation link is invalid.
        </Text>
      </InviteCardShell>
    );
  }

  const acceptAsSignedInUser = () => {
    dispatch({
      type: authActionTypes.ACCEPT_CLIENT_INVITE,
      payload: { inviteId, inviteToken },
    });
  };

  const handleCreateAccountAndAccept = (evt) => {
    evt.preventDefault();
    const nextError = { password: '', confirmPassword: '' };
    if (!PASSWORD_POLICY_REGEX.test(password)) {
      nextError.password = PASSWORD_POLICY_MESSAGE;
    }
    if (password !== confirmPassword) {
      nextError.confirmPassword = 'Passwords must match';
    }
    if (nextError.password || nextError.confirmPassword) {
      setError(nextError);
      return;
    }
    setError({ password: '', confirmPassword: '' });
    dispatch({
      type: authActionTypes.ACCEPT_CLIENT_INVITE,
      payload: { email, password, inviteId, inviteToken },
    });
  };

  if (signedInAsDifferentAccount) {
    return (
      <InviteCardShell>
        <Text
          variant={textVariantsEnum.Text_15}
          fontWeight={fontWeightEnum.Regular}
        >
          You&apos;re signed in as <strong>{currentUser.email}</strong>, but
          this invitation was sent to <strong>{email}</strong>.
        </Text>
        <div className="text-center w-100 mt-3">
          <Button
            variant={buttonVariants.primary_light}
            className={styles.inviteFormButton}
            text="Sign out and continue"
            onClick={() =>
              firebase
                .auth()
                .signOut()
                .then(() => history.go(0))
            }
          />
        </div>
      </InviteCardShell>
    );
  }

  if (currentUser) {
    return (
      <InviteCardShell>
        <Text
          variant={textVariantsEnum.Text_15}
          fontWeight={fontWeightEnum.Regular}
        >
          Accept this invitation as <strong>{currentUser.email}</strong>?
        </Text>
        <div className="text-center w-100 mt-3">
          <Button
            variant={buttonVariants.primary_light}
            className={styles.inviteFormButton}
            text="Accept invitation"
            disabled={signUpAuth.pending}
            loading={signUpAuth.pending}
            onClick={acceptAsSignedInUser}
          />
        </div>
      </InviteCardShell>
    );
  }

  return (
    <InviteCardShell>
      <form
        role="form"
        className={styles.inviteForm}
        onSubmit={handleCreateAccountAndAccept}
      >
        <Text
          variant={textVariantsEnum.Text_15}
          fontWeight={fontWeightEnum.Regular}
        >
          Set a password for <strong>{email}</strong> to continue.
        </Text>
        <Input
          id="password"
          className={styles.input}
          placeholder="Password"
          type="password"
          autoComplete="new-password"
          name="password"
          value={password}
          errorMessage={error.password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          id="confirm_password"
          className={styles.input}
          placeholder="Confirm password"
          type="password"
          autoComplete="new-password"
          name="confirm_password"
          value={confirmPassword}
          errorMessage={error.confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <div className="text-center w-100">
          <Button
            variant={buttonVariants.primary_light}
            className={styles.inviteFormButton}
            type="submit"
            text="Accept invitation"
            disabled={signUpAuth.pending}
            loading={signUpAuth.pending}
          />
        </div>
      </form>
    </InviteCardShell>
  );
};
